/**
 * The payment figures the Principal & Interest "Options" modal quotes.
 *
 * Kept apart from the modal because a figure picked here is **stored** as the loan's P&I, and the
 * amount due, the ACH draft, the statement and every notice then quote it. That makes this the one
 * place in the client that has to agree with the server's own rule for a Dutch loan's scheduled
 * payment (`dutchMonthlyInterest`, commercial-v3-backend `src/utils/dutch-pi.ts`).
 */

export const DAY_BASIS_LABEL: Record<string, string> = {
  '360': '30/360',
  '365': '30/365',
  actual_360: 'Actual/360',
  actual_365: 'Actual/365',
}

/** Level payment that fully amortizes `principal` over `n` months at monthly rate `mr`. */
export const amortized = (principal: number, mr: number, n: number): number | null => {
  if (n <= 0) return null
  if (mr === 0) return principal / n
  return (principal * mr) / (1 - Math.pow(1 + mr, -n))
}

/**
 * Days in the upcoming payment period. The installment does not move with it (see
 * `scheduledMonthlyRate`); it is what the period *accrues* on an Actual/* basis, which is the
 * figure a payoff or an odd-days quote is struck on.
 *
 * Measured on the date parts rather than through `new Date(ymd)`, which parses a bare `YYYY-MM-DD`
 * as UTC midnight and then steps the month in local time: west of UTC that lands in the month
 * before the one meant, so a 10/01 due date reported 31 days (August) instead of 30 (September).
 */
export const periodDays = (nextPaymentDate?: string): number => {
  const parts = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(nextPaymentDate ?? ''))
  if (!parts) return 30
  const [year, month, day] = parts.slice(1).map(Number)
  const end = Date.UTC(year, month - 1, day)
  const start = Date.UTC(month === 1 ? year - 1 : year, month === 1 ? 11 : month - 2, day)
  const d = Math.round((end - start) / 86400000)
  return d > 0 ? d : 30
}

/**
 * The periodic rate a scheduled installment is struck at: a twelfth of a year, on every Daily Rate
 * Basis.
 *
 * The note calls for twelve equal payments, so the length of the period must not move the payment.
 * Quoting days-over-360 instead bills more in a 31-day month than a 30-day one and, over a year,
 * 365 days of interest — and because the quote is stored as the loan's P&I, that difference is
 * billed, drafted and statemented until somebody notices. Longhorn 46519 (TMO 314162) was set to
 * $2,153.27 that way, 31 days of interest on $192,500 at 12.99%, against the $2,083.81 its prior
 * servicer bills.
 */
export const scheduledMonthlyRate = (annualRate: number): number => annualRate / 100 / 12

/** What `principal` accrues over the upcoming period on the loan's Daily Rate Basis. */
export const periodAccrual = (principal: number, annualRate: number, basisKey: string, days: number): number => {
  const denominator = basisKey.endsWith('365') ? 365 : 360
  return (principal * annualRate) / 100 / denominator * days
}

export interface PaymentQuoteOption {
  method: 'Interest Only' | 'Fully Amortized'
  basis: string
  principal: number
  amount: number | null
}

export interface PaymentQuotes {
  options: PaymentQuoteOption[]
  basisKey: string
  /** Days in the upcoming period; 30 on a non-Actual basis, where it does not apply. */
  days: number
  /** What the primary basis accrues over that period. */
  accrual: number
  /** Whether that accrual differs from the installment, i.e. whether it is worth showing. */
  accrualDiffers: boolean
}

/**
 * Non-Dutch payments are calculated on the current unpaid balance. Otherwise the original loan
 * amount is the primary basis, with current UPB offered as an alternative when it differs.
 */
export const paymentQuotes = (input: {
  annualRate: number
  term: number
  originalAmount: number
  currentUpb: number
  isNonDutch: boolean
  dailyRateBasis?: string
  nextPaymentDate?: string
}): PaymentQuotes => {
  const { annualRate, term, originalAmount, currentUpb, isNonDutch } = input
  const basisKey = String(input.dailyRateBasis || '360')
  const days = basisKey.startsWith('actual') ? periodDays(input.nextPaymentDate) : 30
  const mr = scheduledMonthlyRate(annualRate)

  const bases: { label: string; principal: number }[] = isNonDutch
    ? [{ label: 'Current Unpaid Balance', principal: currentUpb || originalAmount }]
    : [{ label: 'Original Loan Amount', principal: originalAmount }]
  if (!isNonDutch && currentUpb > 0 && currentUpb !== originalAmount) {
    bases.push({ label: 'Current UPB', principal: currentUpb })
  }

  const options: PaymentQuoteOption[] = []
  bases.forEach(({ label, principal }) => {
    options.push({ method: 'Interest Only', basis: label, principal, amount: principal * mr })
    options.push({ method: 'Fully Amortized', basis: label, principal, amount: amortized(principal, mr, term) })
  })

  const primary = bases[0]?.principal || 0
  const accrual = periodAccrual(primary, annualRate, basisKey, days)
  return { options, basisKey, days, accrual, accrualDiffers: Math.abs(accrual - primary * mr) >= 0.01 }
}
